import React from 'react';
import { observer } from 'mobx-react';
import { IAction } from '@honeybook/hbui/dist/components/HBModal/HBModal';
import ExclamationIcon from '@honeybook/icons/dist/icons/ExclamationMarkCircle42';
import colors from '@honeybook/hbui/dist/styles/colors';
import { Trans, useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { IFlowTemplate } from '~/types/model.types';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import useAnalytics from '~/common/useAnalytics';
import { FlowRouteType } from '~/utils/flow.utils';
import HBDialog from '@honeybook/hbui/dist/components/HBDialog/HBDialog';
import { useFlowNavigation } from '~/components/Builder/Common/useFlowNavigation';

interface Props {
    isOpen: boolean;
    onEditClick: () => void;
    onUseClick: (template: IFlowTemplate) => void;
    selectedTemplate: IFlowTemplate;
}

const warningModalConfig = {
    contract: {
        translationContext: 'contract',
        editAnalyticsEvent: AnalyticsEvents.WarningModalClickEditContract,
        useAnalyticsEvent: AnalyticsEvents.WarningModalClickUseTemplate,
        goToPageQueryParams: { page: 'contract' as const }
    },
    scheduler: {
        translationContext: 'scheduler',
        editAnalyticsEvent: AnalyticsEvents.WarningModalClickEditTemplate,
        useAnalyticsEvent: AnalyticsEvents.WarningModalClickUseTemplate,
        goToPageQueryParams: { page: 'scheduler' as const }
    }
};

function getWarningModalConfig(template: IFlowTemplate) {
    const hasContract = template.action_block_types.includes('contract_block');
    const hasScheduler = template.action_block_types.includes(
        'schedule_container_block'
    );
    if (hasContract) {
        return warningModalConfig.contract;
    } else if (hasScheduler) {
        return warningModalConfig.scheduler;
    }
}

const SelectedTemplateWarningModal = ({
    isOpen,
    onEditClick,
    onUseClick,
    selectedTemplate
}: Props) => {
    const { t } = useTranslation(NAMESPACES.AUTOMATIONS, {
        keyPrefix: 'step_flow.template_warning_modal'
    });

    const { track } = useAnalytics();
    const { goToFlow } = useFlowNavigation();

    if (!isOpen) {
        return null;
    }

    const {
        translationContext,
        editAnalyticsEvent,
        useAnalyticsEvent,
        goToPageQueryParams
    } = getWarningModalConfig(selectedTemplate);

    const _onEditClick = () => {
        track(editAnalyticsEvent, {
            source: 'automation builder',
            action: 'action'
        });
        goToFlow({
            flowId: selectedTemplate._id,
            forceRoute: FlowRouteType.edit,
            queryParams: goToPageQueryParams,
            openOnNewTab: true
        });
        onEditClick();
    };

    const _onUseClick = () => {
        track(useAnalyticsEvent, {
            source: 'automation builder',
            action: 'action'
        });
        onUseClick(selectedTemplate);
    };

    const actions: IAction[] = [
        {
            text: t('edit_template'),
            theme: 'secondary-invisible-secondary',
            onClick: _onEditClick
        },
        {
            text: t('use_template'),
            theme: 'primary',
            onClick: _onUseClick
        }
    ];

    return (
        <HBDialog
            isOpen={true}
            actions={actions}
            title={t('title')}
            description={
                // @ts-ignore
                <Trans
                    t={t}
                    i18nKey={'subtitle' as any}
                    context={translationContext}
                />
            }
            icon={<ExclamationIcon color={colors.neutral800} />}
            maxWidth="332px"
        />
    );
};

export default observer(SelectedTemplateWarningModal);
